/**
 * CSR (Customer Service Representative) Redux Slice
 *
 * This module manages state for the CSR toolbar and modal interface, which allows
 * customer service representatives to assist customers, manage baskets, and look up
 * vouchers.
 *
 * State Structure:
 * - countries: List of available countries for address forms
 * - customerSearch: Customer search results and pagination state
 * - basketSearch: Basket lookup results and error state
 * - voucherSearch: Voucher search results or error messages
 * - assisting: Currently assisted customer and their basket
 * - views: View stack for the modal navigation system
 *
 * View Stack Pattern:
 * The `views` slice uses an unconventional pattern of storing JSX elements in Redux state.
 * This allows the CSR modal to maintain a stack-based navigation system where users can
 * drill down into details (e.g., Customer List > Customer Details > Edit Address) and
 * use a "back" button to navigate up the stack.
 *
 * While storing JSX in Redux is generally not recommended (Redux prefers serializable state),
 * this pattern works here because:
 * 1. React elements are immutable
 * 2. The state is ephemeral (modal closes when CSR logs out or reloads page)
 * 3. The pattern has been stable and working in production
 *
 * For new features, consider using view identifiers/routes instead of storing JSX components.
 */
import {
    PayloadAction,
    Reducer,
    combineReducers,
    createSlice,
} from "@reduxjs/toolkit";
import { castDraft } from "immer";
import type { JSX } from "react";

import { IVoucherSearchErrors, IVoucherSearchResults } from "./models";
import { IReduxState } from "./reducers.interfaces";
import {
    loadCountries,
    loadCurrentlyAssistedBasket,
    loadCurrentlyAssistedCustomer,
    lookupBasket,
    searchCustomers,
    searchVoucherCode,
    startAssistingCustomer,
    stopAssistingCustomer,
} from "./thunks";

const countriesDefault: IReduxState["countries"] = [];

const customerSearchDefault: IReduxState["customerSearch"] = {
    total: 0,
    page: 1,
    hasNext: false,
    hasPrev: false,
    results: [],
    term: "",
};

const basketSearchDefault: IReduxState["basketSearch"] = {
    term: "",
    error: "",
    basket: null,
    owner: null,
};

type IVoucherSearch = IVoucherSearchResults | IVoucherSearchErrors;

const voucherSearchDefault: IVoucherSearch = {
    count: 0,
    next: null,
    previous: null,
    results: [],
};

const assistingDefault: IReduxState["assisting"] = {
    customer: null,
    basket: null,
    isAssistLoading: false,
};

const viewsDefault: IReduxState["views"] = {
    stack: [],
};

// Countries slice
const countriesSlice = createSlice({
    name: "csr/countries",
    initialState: countriesDefault,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(loadCountries.fulfilled, (state, action) => {
            return action.payload;
        });
    },
});

// Customer Search slice
const customerSearchSlice = createSlice({
    name: "csr/customerSearch",
    initialState: customerSearchDefault,
    reducers: {
        setCustomerSearchTerm: (state, action: PayloadAction<string>) => {
            state.term = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(searchCustomers.fulfilled, (state, action) => {
            state.total = action.payload.total;
            state.page = action.payload.page;
            state.hasNext = action.payload.hasNext;
            state.hasPrev = action.payload.hasPrev;
            state.results = castDraft(action.payload.customers);
        });
    },
});

// Basket Search slice
const basketSearchSlice = createSlice({
    name: "csr/basketSearch",
    initialState: basketSearchDefault,
    reducers: {
        setBasketSearchTerm: (state, action: PayloadAction<string>) => {
            state.term = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(lookupBasket.fulfilled, (state, action) => {
                state.basket = castDraft(action.payload.basket);
                state.owner = castDraft(action.payload.owner);
                state.error = "";
            })
            .addCase(lookupBasket.rejected, (state, action) => {
                state.basket = null;
                state.owner = null;
                const payloadMessage =
                    typeof action.payload === "string"
                        ? action.payload
                        : undefined;
                state.error =
                    payloadMessage ||
                    action.error.message ||
                    "An error occurred while looking up the basket";
            });
    },
});

// Assisting slice
const assistingSlice = createSlice({
    name: "csr/assisting",
    initialState: assistingDefault,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                loadCurrentlyAssistedCustomer.fulfilled,
                (state, action) => {
                    state.customer = castDraft(action.payload);
                },
            )
            .addCase(loadCurrentlyAssistedBasket.fulfilled, (state, action) => {
                state.basket = castDraft(action.payload);
            })
            .addCase(startAssistingCustomer.pending, (state) => {
                state.isAssistLoading = true;
            })
            .addCase(startAssistingCustomer.fulfilled, (state, action) => {
                state.isAssistLoading = false;
                state.customer = castDraft(action.payload.customer);
                state.basket = castDraft(action.payload.basket);
            })
            .addCase(startAssistingCustomer.rejected, (state) => {
                state.isAssistLoading = false;
            })
            .addCase(stopAssistingCustomer.pending, (state) => {
                state.isAssistLoading = true;
            })
            .addCase(stopAssistingCustomer.fulfilled, (state, action) => {
                state.isAssistLoading = false;
                state.customer = castDraft(action.payload.customer);
                state.basket = castDraft(action.payload.basket);
            })
            .addCase(stopAssistingCustomer.rejected, (state) => {
                state.isAssistLoading = false;
            });
    },
});

// Voucher Search slice
const voucherSearchSlice = createSlice({
    name: "csr/voucherSearch",
    // The lazy-initializer form with an explicit return type makes createSlice
    // infer the full IVoucherSearch union as its state type. Referencing
    // voucherSearchDefault directly would not: TS control-flow narrows the
    // const to its initializer's variant (IVoucherSearchResults).
    initialState: (): IVoucherSearch => voucherSearchDefault,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(searchVoucherCode.pending, () => {
                return voucherSearchDefault;
            })
            .addCase(searchVoucherCode.fulfilled, (state, action) => {
                return action.payload;
            })
            .addCase(searchVoucherCode.rejected, (state, action) => {
                const detail =
                    (action.payload &&
                        typeof action.payload.detail === "string" &&
                        action.payload.detail) ||
                    action.error.message ||
                    "An error occurred while searching for voucher";
                return {
                    detail,
                };
            });
    },
});

// Views slice
const viewsSlice = createSlice({
    name: "csr/views",
    initialState: viewsDefault,
    reducers: {
        viewStackClear: () => {
            return { stack: [] };
        },
        viewStackSetRoot: (
            state,
            action: PayloadAction<{ name: string; component: JSX.Element }>,
        ) => {
            state.stack = [
                {
                    name: action.payload.name,
                    component: action.payload.component,
                },
            ];
        },
        viewStackPush: (
            state,
            action: PayloadAction<{ name: string; component: JSX.Element }>,
        ) => {
            state.stack.push({
                name: action.payload.name,
                component: action.payload.component,
            });
        },
        viewStackPop: (state) => {
            if (state.stack.length >= 1) {
                state.stack.pop();
            }
        },
        viewStackReplaceTop: (
            state,
            action: PayloadAction<{ name: string; component: JSX.Element }>,
        ) => {
            if (state.stack.length >= 1) {
                state.stack.pop();
            }
            state.stack.push({
                name: action.payload.name,
                component: action.payload.component,
            });
        },
    },
});

// Export action creators
export const { setCustomerSearchTerm } = customerSearchSlice.actions;
export const { setBasketSearchTerm } = basketSearchSlice.actions;
export const {
    viewStackClear,
    viewStackSetRoot,
    viewStackPush,
    viewStackPop,
    viewStackReplaceTop,
} = viewsSlice.actions;

// Combine reducers
// Cast to Reducer<IReduxState> because combineReducers infers mutable state
// but our IReduxState uses Immutable<> wrapper. The reducer implementation
// correctly handles both mutable drafts (inside Immer) and immutable reads.
export const reducers: Reducer<IReduxState> = combineReducers({
    countries: countriesSlice.reducer,
    customerSearch: customerSearchSlice.reducer,
    basketSearch: basketSearchSlice.reducer,
    voucherSearch: voucherSearchSlice.reducer,
    assisting: assistingSlice.reducer,
    views: viewsSlice.reducer,
});
